'use client';
import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { MapPin, BedDouble, Bath, Maximize2, Star, Eye, Phone, Calendar, ShieldCheck } from 'lucide-react';
import type { Property } from './mockData';
import { TYPE_LABELS, formatPrice } from './mockData';

interface Props {
  property: Property;
  viewMode: 'grid' | 'list';
  onUnlock: () => void;
  ownerBadge?: 'seller' | 'buyer' | null;
}

export default function PropertyCard({ property, viewMode, onUnlock, ownerBadge }: Props) {
  const [imgError, setImgError] = useState(false);

  const modeColor = property.mode === 'sale' ?'bg-emerald-100 text-emerald-700' :'bg-blue-100 text-blue-700';
  const modeLabel = property.mode === 'sale' ? 'للبيع' : 'للإيجار';

  const daysAgo = Math.floor(
    (new Date().getTime() - new Date(property.listedAt).getTime()) / (1000 * 60 * 60 * 24)
  );
  const dateLabel = daysAgo === 0 ? 'اليوم' : daysAgo === 1 ? 'أمس' : `منذ ${daysAgo} أيام`;

  const VerifiedBadge = () => {
    if (!ownerBadge && !property.verified) return null;
    const isSeller = ownerBadge === 'seller' || property.verified;
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-600 px-2 py-0.5 rounded-full ${
        isSeller ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'
      }`}>
        <ShieldCheck size={11} />
        {isSeller ? 'بائع موثق' : 'مشتري موثق'}
      </span>
    );
  };

  if (viewMode === 'list') {
    return (
      <div className="group bg-white rounded-xl border border-gray-100 shadow-card hover:shadow-card-hover transition-all duration-200 flex gap-0 overflow-hidden">
        <div className="relative w-48 flex-shrink-0">
          <AppImage
            src={imgError ? '/assets/images/no_image.png' : property.image}
            alt={property.imageAlt}
            fill
            className="object-cover"
            onError={() => setImgError(true)}
          />
          {property.status === 'featured' && (
            <span className="absolute top-2 right-2 flex items-center gap-1 bg-accent-500 text-white text-xs font-600 px-2 py-0.5 rounded-full">
              <Star size={10} fill="white" />
              مميز
            </span>
          )}
        </div>
        <div className="flex-1 p-4 flex flex-col justify-between">
          <div>
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="text-sm font-600 text-gray-900 leading-snug line-clamp-2">{property.title}</h3>
              <span className={`flex-shrink-0 text-xs font-600 px-2 py-0.5 rounded-full ${modeColor}`}>{modeLabel}</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
              <MapPin size={12} />
              {property.city} — {property.district}
            </div>
            {(ownerBadge || property.verified) && (
              <div className="mb-2">
                <VerifiedBadge />
              </div>
            )}
            <div className="flex items-center gap-4 text-xs text-gray-600">
              {property.bedrooms !== null && (
                <span className="flex items-center gap-1"><BedDouble size={13} className="text-gray-400" />{property.bedrooms}</span>
              )}
              {property.bathrooms !== null && (
                <span className="flex items-center gap-1"><Bath size={13} className="text-gray-400" />{property.bathrooms}</span>
              )}
              <span className="flex items-center gap-1"><Maximize2 size={13} className="text-gray-400" />{property.area} م²</span>
              {property.furnished && <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-500">مفروشة</span>}
            </div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <div>
              <span className="text-base font-700 text-gray-900 tabular-nums">{formatPrice(property.price, property.priceUnit)}</span>
              <span className="text-xs text-gray-400 mr-1">({Math.round(property.price / property.area).toLocaleString('ar-SA')} SAR/م²)</span>
            </div>
            <button
              onClick={onUnlock}
              className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-xs font-600 rounded-lg transition-all duration-150 active:scale-95"
            >
              <Phone size={13} />
              عرض رقم الهاتف
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group bg-white rounded-xl border border-gray-100 shadow-card hover:shadow-card-hover transition-all duration-200 overflow-hidden flex flex-col">
      {/* Image */}
      <div className="relative h-48 overflow-hidden bg-gray-100">
        <AppImage
          src={imgError ? '/assets/images/no_image.png' : property.image}
          alt={property.imageAlt}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-300"
          onError={() => setImgError(true)}
        />
        {/* Badges */}
        <div className="absolute top-3 right-3 flex flex-col gap-1.5">
          <span className={`text-xs font-600 px-2.5 py-1 rounded-full backdrop-blur-sm ${modeColor}`}>{modeLabel}</span>
          {property.status === 'featured' && (
            <span className="flex items-center gap-1 bg-accent-500 text-white text-xs font-600 px-2.5 py-1 rounded-full">
              <Star size={10} fill="white" />
              مميز
            </span>
          )}
        </div>
        {property.furnished && (
          <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-amber-700 text-xs font-600 px-2 py-0.5 rounded-full border border-amber-200">
            مفروشة
          </span>
        )}
        {/* Views */}
        <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">
          <Eye size={11} />
          {property.views.toLocaleString('ar-SA')}
        </div>
        {/* Type */}
        <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-600 px-2 py-0.5 rounded-full">
          {TYPE_LABELS[property.type]}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 p-4 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-sm font-600 text-gray-900 leading-snug line-clamp-2 flex-1">{property.title}</h3>
          {(ownerBadge || property.verified) && (
            <ShieldCheck size={16} className={ownerBadge === 'buyer' ? 'text-emerald-500' : 'text-blue-500'} title="موثق" />
          )}
        </div>

        <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
          <MapPin size={12} />
          {property.city} — {property.district}
        </div>

        {(ownerBadge || property.verified) && (
          <div className="mb-2">
            <VerifiedBadge />
          </div>
        )}

        {/* Specs */}
        <div className="flex items-center gap-3 text-xs text-gray-600 mb-3">
          {property.bedrooms !== null && (
            <span className="flex items-center gap-1">
              <BedDouble size={13} className="text-gray-400" />
              {property.bedrooms} غرف
            </span>
          )}
          {property.bathrooms !== null && (
            <span className="flex items-center gap-1">
              <Bath size={13} className="text-gray-400" />
              {property.bathrooms} حمام
            </span>
          )}
          <span className="flex items-center gap-1">
            <Maximize2 size={13} className="text-gray-400" />
            {property.area} م²
          </span>
        </div>

        {/* Price */}
        <div className="mt-auto">
          <div className="mb-3">
            <span className="text-lg font-700 text-gray-900 tabular-nums">{formatPrice(property.price, property.priceUnit)}</span>
            <div className="text-xs text-gray-400">{Math.round(property.price / property.area).toLocaleString('ar-SA')} SAR/م²</div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <Calendar size={11} />
              {dateLabel}
            </div>
            <button
              onClick={onUnlock}
              className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-xs font-600 rounded-lg transition-all duration-150 active:scale-95 shadow-sm"
            >
              <Phone size={13} />
              عرض رقم الهاتف
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}